import type { FailedInvite } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';

import _t from 'i18n!nls/enterprise-admin-learners';

// Prevents CSV formula injection: a cell starting with =, +, -, or @ is treated as a
// formula by Excel/LibreOffice, so a malicious email or server message could otherwise
// execute arbitrary commands when the admin opens this file.
const CSV_FORMULA_TRIGGER_CHARS = ['=', '+', '-', '@'];
const sanitizeCsvFormulaTrigger = (cell: string) =>
  CSV_FORMULA_TRIGGER_CHARS.includes(cell.charAt(0)) ? `'${cell}` : cell;

const escapeCsvCell = (cell: string) => `"${sanitizeCsvFormulaTrigger(cell).replace(/"/g, '""')}"`;

// Friendlier copy for the error codes admins hit most often — the raw backend `message` for
// these can reference internal details (e.g. a program id) rather than something meant for an
// admin to read directly. Both the invite flow and Add to Group run the same
// `ProgramInvitations_AddUsersToProgram` mutation, so they share this error code taxonomy.
const getFriendlyMessage = (invite: FailedInvite) => {
  switch (invite.errorCode) {
    case 'PROGRAM_INVITEE_ERROR_EXISTING_INVITATION_FOR_EMAIL':
    case 'PROGRAM_INVITEE_ERROR_EXISTING_INVITATION_FOR_EXTERNAL_ID':
      return _t('This learner has already been invited.');
    case 'PROGRAM_INVITEE_ERROR_EXISTING_MEMBERSHIP_FOR_EMAIL':
    case 'PROGRAM_INVITEE_ERROR_EXISTING_MEMBERSHIP_FOR_EXTERNAL_ID':
      return _t('This learner is already a member of this group.');
    default:
      return invite.message;
  }
};

/**
 * Generates and downloads a client-side CSV of failed invites — there's no server-generated
 * report for this mutation. Also reused by Add to Group's equivalent breakdown dialog, since
 * both flows fail with the same `{ email, errorCode, message }` per-invitee shape.
 */
export const downloadInviteErrorReport = (failed: FailedInvite[], filename = 'invite-errors.csv') => {
  const rows = [
    [_t('Email'), _t('Error code'), _t('Message')],
    ...failed.map((invite) => [invite.email, invite.errorCode, getFriendlyMessage(invite)]),
  ];
  const csv = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  window.setTimeout(() => {
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  }, 0);
};
